import type {
  ApiSelect,
  ApiSelectCustom,
  ApiSelectObsoleto,
} from "@/types/apiSelect";

/**
 * Union of every select field name views can subscribe to via
 * `useSelectFields`. Names are kept identical to the legacy
 * `useSelectContext` shape to minimise churn on consumers.
 */
export type SelectStoreField =
  | "categorie"
  | "clienti"
  | "filiali"
  | "motiviChangeOver"
  | "opzioniCompleanno"
  | "parcoNoleggio"
  | "parcoNoleggioPreparazione"
  | "raggruppamenti"
  | "raggruppamentiL1"
  | "raggruppamentiL2"
  | "tipi"
  | "listini"
  | "unitaMisura"
  | "codiceMacchina2"
  | "catVerbale"
  | "accessori"
  | "macchine"
  | "meccanico"
  | "agente"
  | "aliquoteIva"
  | "tipiRiga"
  | "tipiRigaPreventivi"
  | "vociDocumento"
  | "stati"
  | "pagamenti"
  | "utenti"
  | "preparazioni"
  | "parcoInterno";

/**
 * Discriminated union mirroring the three flavors handled by the old
 * `DataLoader.selectConfig`:
 *
 *  - default       → `apiSelect(path, { realPath: true, filters })` returns `ApiSelect[]`
 *  - `custom: true`→ `apiSelect(path, { realPath: true, filters })`, then mapped to
 *                    `ApiSelectCustom` (`id: String(row.id)`).
 *  - `obsoleto: true` → `apiSelectExtended<ApiSelectObsoleto>(path, { filters })`.
 *
 * NOTE: `obsoleto` paths already include `/select/extended` in the registry and
 * are called as-is (no realPath wrapping).
 */
export type SelectRegistryEntry =
  | {
      path: string;
      filters?: object;
      storeField: SelectStoreField;
      custom?: false;
      obsoleto?: false;
    }
  | {
      path: string;
      filters?: object;
      storeField: SelectStoreField;
      custom: true;
      obsoleto?: false;
    }
  | {
      path: string;
      filters?: object;
      storeField: SelectStoreField;
      custom?: false;
      obsoleto: true;
    };

/**
 * Keys = the "select key" as broadcasted by the SignalR `RefreshSelect` event
 * (1:1 with the old `selectConfig` in `DataLoader.tsx`).
 */
export const selectRegistry = {
  "parco/categorie/select": {
    path: "parco/categorie/select",
    storeField: "categorie",
  },
  "clienti/select": {
    path: "clienti/select",
    storeField: "clienti",
  },
  "filiali/select": {
    path: "filiali/select/extended",
    storeField: "filiali",
    obsoleto: true,
  },
  "documenti/change-over/motivi/select": {
    path: "documenti/change-over/motivi/select",
    storeField: "motiviChangeOver",
  },
  "parco-noleggio/select": {
    path: "parco-noleggio/select",
    storeField: "parcoNoleggio",
  },
  "parco-noleggio/preparazione/select": {
    path: "parco-noleggio/preparazione/select",
    storeField: "parcoNoleggioPreparazione",
  },
  "parco/raggruppamenti/select": {
    path: "parco/raggruppamenti/select",
    storeField: "raggruppamenti",
  },
  "clienti/riferimenti/opzioni-compleanno": {
    path: "clienti/riferimenti/opzioni-compleanno",
    storeField: "opzioniCompleanno",
  },
  "raggruppamenti/1/select": {
    path: "raggruppamenti/1/select",
    storeField: "raggruppamentiL1",
  },
  "raggruppamenti/2/select": {
    path: "raggruppamenti/2/select",
    storeField: "raggruppamentiL2",
  },
  "domande-verbale/tipi/select": {
    path: "domande-verbale/tipi/select",
    storeField: "tipi",
  },
  "listini/select": {
    path: "listini/select",
    storeField: "listini",
  },
  "unita-misure/select": {
    path: "unita-misure/select",
    storeField: "unitaMisura",
  },
  "registrazione-vendite/codici-macchina/select": {
    path: "registrazione-vendite/codici-macchina/select",
    storeField: "codiceMacchina2",
    custom: true,
  },
  catVerbale: {
    path: "parco/categorie/select",
    filters: { flagVerbale: true },
    storeField: "catVerbale",
  },
  "parco/collegamenti/accessori/select": {
    path: "parco/collegamenti/accessori/select",
    storeField: "accessori",
  },
  "parco/collegamenti/macchine/select": {
    path: "parco/collegamenti/macchine/select",
    storeField: "macchine",
  },
  "utente/meccanici/select": {
    path: "utente/meccanici/select",
    storeField: "meccanico",
  },
  "agenti/select": {
    path: "agenti/select/extended",
    storeField: "agente",
    obsoleto: true,
  },
  "aliquotaiva/select": {
    path: "aliquotaiva/select",
    storeField: "aliquoteIva",
  },
  "documenti/righe/tipi/select": {
    path: "documenti/righe/tipi/select",
    storeField: "tipiRiga",
  },
  "documenti/preventivi/righe/tipi/select": {
    path: "documenti/preventivi/righe/tipi/select",
    storeField: "tipiRigaPreventivi",
  },
  "documenti/voci/select": {
    path: "documenti/voci/select",
    storeField: "vociDocumento",
  },
  "documenti/preventivi/stati/select": {
    path: "documenti/preventivi/stati/select",
    storeField: "stati",
  },
  "pagamenti/select": {
    path: "pagamenti/select",
    storeField: "pagamenti",
  },
  "utente/select": {
    path: "utente/select",
    storeField: "utenti",
  },
  "preparazioni/select": {
    path: "preparazioni/select",
    storeField: "preparazioni",
  },
  "parco/interno/select": {
    path: "parco-noleggio/uso-interno/select",
    storeField: "parcoInterno",
  },
} as const satisfies Record<string, SelectRegistryEntry>;

export type SelectRegistryKey = keyof typeof selectRegistry;

export const selectRegistryKeys = Object.keys(
  selectRegistry,
) as SelectRegistryKey[];

export function isSelectRegistryKey(key: string): key is SelectRegistryKey {
  return Object.prototype.hasOwnProperty.call(selectRegistry, key);
}

/**
 * Reverse lookup: from a Zustand store field name to the matching registry key.
 * Built statically so consumers can stay typed.
 */
type _StoreFieldToKey = {
  [K in SelectRegistryKey]: (typeof selectRegistry)[K]["storeField"];
};

type InvertedMap = {
  [F in SelectStoreField]: {
    [K in SelectRegistryKey]: _StoreFieldToKey[K] extends F ? K : never;
  }[SelectRegistryKey];
};

export const selectStoreFieldToRegistryKey: InvertedMap = (() => {
  const out = {} as Record<SelectStoreField, SelectRegistryKey>;
  for (const key of selectRegistryKeys) {
    const entry = selectRegistry[key];
    out[entry.storeField] = key;
  }
  return out as InvertedMap;
})();

/**
 * Row type produced by each store field, after the fetcher has applied the
 * proper transform:
 *
 *  - `codiceMacchina2` → `ApiSelectCustom[]`
 *  - `filiali`, `agente` → `ApiSelectObsoleto[]`
 *  - everything else → `ApiSelect[]`
 */
export type SelectFieldData = {
  categorie: ApiSelect[];
  clienti: ApiSelect[];
  filiali: ApiSelectObsoleto[];
  motiviChangeOver: ApiSelect[];
  opzioniCompleanno: ApiSelect[];
  parcoNoleggio: ApiSelect[];
  parcoNoleggioPreparazione: ApiSelect[];
  raggruppamenti: ApiSelect[];
  raggruppamentiL1: ApiSelect[];
  raggruppamentiL2: ApiSelect[];
  tipi: ApiSelect[];
  listini: ApiSelect[];
  unitaMisura: ApiSelect[];
  codiceMacchina2: ApiSelectCustom[];
  catVerbale: ApiSelect[];
  accessori: ApiSelect[];
  macchine: ApiSelect[];
  meccanico: ApiSelect[];
  agente: ApiSelectObsoleto[];
  aliquoteIva: ApiSelect[];
  tipiRiga: ApiSelect[];
  tipiRigaPreventivi: ApiSelect[];
  vociDocumento: ApiSelect[];
  stati: ApiSelect[];
  pagamenti: ApiSelect[];
  utenti: ApiSelect[];
  preparazioni: ApiSelect[];
  parcoInterno: ApiSelect[];
};
